<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import Input from './OdooInput.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const store = useStore();

const selectedModel = ref('');
const keyMapping = ref({});
const selectedFields = ref([{ field: '', value: '', required: false }]);

const models = computed(() => store.state.odoo.models);
const fields = computed(() => store.state.odoo.fields);

const hasFields = computed(() => Object.keys(fields.value).length > 0);

watch(selectedModel, newModel => {
  if (newModel) {
    store.dispatch('odoo/fetchOdooFields', newModel);
  }
});

watch(
  () => props.modelValue,
  newValue => {
    if (newValue) {
      selectedModel.value = newValue.model || '';
      keyMapping.value = newValue.keyMapping || {};
      selectedFields.value = Object.keys(newValue.keyMapping || {}).map(
        key => ({
          field: key,
          value: newValue.keyMapping[key],
          required: fields.value[key]?.required || false,
        })
      );
      if (selectedFields.value.length === 0) {
        selectedFields.value = [{ field: '', value: '', required: false }];
      }
    }
  },
  { immediate: true }
);

watch(fields, newFields => {
  if (Object.keys(newFields).length === 0) {
    return;
  }

  const newFieldsWithKey = Object.keys(newFields).map(key => ({
    key,
    ...newFields[key],
  }));
  const requiredFields = Object.values(newFieldsWithKey).filter(
    field => field.required
  );
  requiredFields.forEach(field => {
    if (!selectedFields.value.find(item => item.field === field.key)) {
      selectedFields.value.push({
        field: field.key,
        value: '',
        required: true,
      });
    }
  });
});

const addField = () => {
  selectedFields.value.push({ field: '', value: '', required: false });
};

const updateField = (index, key, value) => {
  selectedFields.value[index][key] = value;
  updateModelValue();
};

const updateModelValue = () => {
  const keyMappingValue = {};
  selectedFields.value.forEach(item => {
    if (item.field) {
      keyMappingValue[item.field] = item.value;
    }
  });
  emit('update:modelValue', {
    model: selectedModel.value,
    keyMapping: keyMappingValue,
  });
};

const removeField = index => {
  if (
    selectedFields.value.length > 1 &&
    !selectedFields.value[index].required
  ) {
    selectedFields.value.splice(index, 1);
    updateModelValue();
  }
};

onMounted(() => {
  store.dispatch('odoo/fetchOdooModels');
});
</script>

<template>
  <div class="mt-4">
    <label for="model-select">Select Odoo Model:</label>
    <select
      id="model-select"
      v-model="selectedModel"
      @change="updateModelValue"
    >
      <option v-for="model in models" :key="model.model" :value="model.model">
        {{ model.name }}
      </option>
    </select>

    <div v-if="hasFields">
      <p>Define Fields</p>
      <div v-for="(item, index) in selectedFields" :key="index">
        <div class="flex flex-row items-baseline gap-2">
          <select
            v-model="item.field"
            :disabled="item.required"
            @change="updateField(index, 'field', $event.target.value)"
          >
            <option v-for="(field, key) in fields" :key="key" :value="key">
              {{ field.string }} ({{ key }})
            </option>
          </select>
          <p>to</p>
          <Input
            v-model="item.value"
            class="w-full"
            placeholder="Enter Value"
            @change="updateField(index, 'value', $event.target.value)"
          />
          <woot-button
            v-if="!item.required"
            icon="dismiss-circle"
            variant="smooth"
            size="tiny"
            color-scheme="alert"
            @click="removeField(index)"
          />
        </div>
      </div>

      <button @click="addField">Add Field</button>
    </div>
  </div>
</template>

<style scoped>
/* Add your styles here */
</style>
